import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router';

import {
  IonToolbar,
  IonContent,
  IonPage,
  IonTitle,
  IonMenuButton,
  IonButton,
  IonButtons,
  IonHeader,
  IonList,
  IonListHeader,
  IonItem,
  IonInput,
  IonLoading,
  getConfig,
  useIonToast
} from '@ionic/react';

import './ShippingPage.scss';
import { connect } from '../data/connect';
import { Shipping } from '../models/Shipping';
import ShippingList from '../components/ShippingList';
import axiosInstance from '../data/axios/axiosInstance';
import ShippingCard from '../components/ShippingCard';

interface OwnProps {
  onTabChange: (event: CustomEvent) => void;
}

interface StateProps {
  mode: 'ios' | 'md';
  isAuthenticated: boolean;
}

interface DispatchProps {
}

type ShippingPageProps = OwnProps & StateProps & DispatchProps;

const ShippingPage: React.FC<ShippingPageProps> = ({
  isAuthenticated,
  onTabChange,
}) => {
  const [barcode, setBarcode] = useState('');
  const [shippingNum, setShippingNum] = useState('');
  const [shipping, setShipping] = useState<Shipping>();
  const [count, setCount] = useState(0);
  const [shippings, setShippings] = useState<Shipping[]>([]);
  const [segment, setSegment] = useState("shipping");
  const [loading, setLoading] = useState(false);
  const pageRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLIonInputElement>(null);
  const shippingRef = useRef<HTMLIonInputElement>(null);
  const [presentToast] = useIonToast();
  const location = useLocation();

  const showToast = (message: string, duration = 3000, focusRef?: React.RefObject<any>) => {
    presentToast({
      message,
      duration,
      position: 'middle',
      buttons: [
        {
          text: 'Close',
          role: 'cancel',
         handler: () => {
            // Close 押下後にフォーカスを戻す
          if (focusRef) {
            setTimeout(() => {
              if (typeof focusRef.current?.setFocus === 'function') {
                focusRef.current.setFocus();
              } else if (typeof focusRef.current?.open === 'function') {
                focusRef.current.open();
              }
            }, 50);
          }
          },          
        },
      ],
    });
  };
// 送り状番号の正規化処理
const normalizeShippingNum = (value: string): string => {
  let shippingNum = value.trim().toUpperCase();

  // FedEx特殊形式
  // 123456789012NNNN → 123456789012
  const fedexExtraPattern = /^([0-9]{12})[0-9]{4}$/;

  const match = shippingNum.match(fedexExtraPattern);

  if (match) {
    shippingNum = match[1];
  }
  return shippingNum;
};

const validateShippingNum = (value: string): boolean => {
  const shippingNum = value.trim().toUpperCase();

  // EMS / eパケット
  // EE123456789JP
  const emsPattern = /^[A-Z]{2}[0-9]{9}[A-Z]{2}$/;

  // DHL
  // 1234567890
  const dhlPattern = /^[0-9]{10}$/;

  // FedEx
  // 123456789012
  const fedexPattern = /^[0-9]{12}$/;

  // UPS
  // 1Z6854A80415173564
  const upsPattern = /^1Z[A-Z0-9]{16}$/;

  return (
    emsPattern.test(shippingNum) ||
    dhlPattern.test(shippingNum) ||
    fedexPattern.test(shippingNum) ||
    upsPattern.test(shippingNum));
  };

  useEffect(() => {
    console.log("focus");
    shippingRef.current?.setFocus();
  }, [shipping]);

  const playBuzzer = () => {
    const buzzer = document.getElementById('buzzer') as HTMLAudioElement;
    if (buzzer) {
      buzzer.play();
    }
  };

  const playRing = () => {
    const ring = document.getElementById('ring') as HTMLAudioElement;
    if (ring) {
      ring.play();
    }
  };

  const handleBarcodeChange = async (event: CustomEvent) => {
    setBarcode((event.target as HTMLInputElement).value.toUpperCase());
  };

  const handleKeyPress = async (event: React.KeyboardEvent<HTMLIonInputElement>) => {
    if (event.key === 'Enter') {
      if ( barcode !== "" ) {
        setLoading(true);

        try {
          const response = await axiosInstance.get('/shipping/', { params: { barcode } });
          if (response.status === 200 && response.data.shipping != null) {
            setLoading(false);
            setShipping(response.data.shipping);
            playRing();
          } else {
            inputRef.current?.setFocus();
            showToast("バーコード入力エラー", 3000, inputRef);
            playBuzzer();
            setLoading(false);
          }
        } catch (error) {
          console.error('Error processing shippings:', error);
          playBuzzer();
          setLoading(false);
        }
      }
    }
  };

  const handleShippingChange = (event: CustomEvent) => {
    setShippingNum((event.target as HTMLInputElement).value.toUpperCase());
  };

  const handleSearch = async () => {
    if ( barcode !== "" ) {
      setLoading(true);

      try {
        const response = await axiosInstance.get('/shipping/', { params: { barcode } });
        if (response.status === 200 && response.data.shipping != null) {
          setLoading(false);
          setShipping(response.data.shipping);          
          playRing();
        } else {
          setLoading(false);
          inputRef.current?.setFocus();
          showToast("バーコード入力エラー", 3000, inputRef);
          playBuzzer();
        }
      } catch (error) {
        console.error('Error processing shippings:', error);
        playBuzzer();
        setLoading(false);
      }
    }
  };

  const handleProcess = async () => {
    const normalizedShippingNum = normalizeShippingNum(shippingNum);
    if (!validateShippingNum(normalizedShippingNum)) {
      showToast("送り状番号の形式が不正です、EMS/DHL/FedEx/UPS形式を確認してください。", 3000, shippingRef);
      shippingRef.current?.setFocus();
      playBuzzer();
      return;
    }

    if (shipping) {
      setLoading(true);
      try {
        const response = await axiosInstance.post('/shippings/', { id: shipping?.id, shipping_num: normalizedShippingNum });
        if (response.status === 200) {
          if (response.data.result !== "success") {
            setShipping(undefined);
            setCount((prevCount) => prevCount - 1);

            inputRef.current?.setFocus();
          } else if (response.data.result === "success") {
            setShippings(response.data.shippings);
            setSegment("success");
          }
          setBarcode("");
          setShippingNum("")
          setLoading(false);
          playRing();
        } else {
          setBarcode("");
          setShippingNum("")
          setLoading(false);
          playBuzzer();
        }
      } catch (error) {
        console.error('Error processing inspections:', error);
        setBarcode("");
        setShippingNum("")
        setLoading(false);
        playBuzzer();
      }
    }
  };

  const handleShippingKeyPress = async (event: React.KeyboardEvent<HTMLIonInputElement>) => {
    if (event.key === 'Enter') {
      const normalizedShippingNum = normalizeShippingNum(shippingNum);
      if (!validateShippingNum(normalizedShippingNum)) {
        showToast("送り状番号の形式が不正です、EMS/DHL/FedEx/UPS形式を確認してください。", 3000, shippingRef);
        shippingRef.current?.setFocus();        
        playBuzzer();
        return;
      }      
      if (shipping) {
        setLoading(true);
        try {
          const response = await axiosInstance.post('/shippings/', { id: shipping?.id, shipping_num: normalizedShippingNum });
          if (response.status === 200) {
            if (response.data.result !== "success") {
              setShipping(undefined);
              setCount((prevCount) => prevCount - 1);
  
              inputRef.current?.setFocus();
            } else if (response.data.result === "success") {
              setShippings(response.data.shippings);
              setSegment("success");
            }
            setBarcode("");
            setShippingNum("")
            setLoading(false);
            playRing();
          } else {
            setBarcode("");
            setShippingNum("")
            setLoading(false);
            playBuzzer();
          }
        } catch (error) {
          console.error('Error processing inspections:', error);
          setBarcode("");
          setShippingNum("")
          setLoading(false);
          playBuzzer();
        }
      }
    }
  };

  const handleClear = () => {
    setBarcode("");
    setShippingNum("");
    setShipping(undefined);
    inputRef.current?.setFocus();
  }

  const fetchShippings = async () => {
    try {
      const response = await axiosInstance.get('/shipping');
      if (response.status === 200) {
        setCount(response.data.count);

        inputRef.current?.setFocus();
      }
    } catch (error) {
      console.error('Error fetching shippings:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated && location.pathname.includes("shipping")) {
      fetchShippings();
    }
  }, [isAuthenticated, onTabChange]);

  return (
    <IonPage ref={pageRef} id="shipping-page">
      <IonHeader translucent={true}>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>出荷作業</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen={true}>
        <audio id="buzzer" src="/assets/error.mp3" preload="auto"></audio>
        <audio id="ring" src="/assets/ok.mp3" preload="auto"></audio>
        <IonList>
          <IonItem>
            <IonInput label="オーダー番号" ref={inputRef} type="text" placeholder="000" value={barcode} onIonInput={handleBarcodeChange} onKeyDown={handleKeyPress}></IonInput>
            <IonButton color="primary" expand="full" slot='end' onClick={handleClear}>クリア</IonButton>
            <IonButton color="primary" expand="full" slot='end' onClick={handleSearch}>検　索</IonButton>
          </IonItem>
          <IonItem>
            <IonInput label="送り状番号" ref={shippingRef} type="text" placeholder="000" value={shippingNum} onIonInput={handleShippingChange} onKeyDown={handleShippingKeyPress}></IonInput>
            <IonButton color="primary" expand="full" slot='end' onClick={handleProcess}>OK</IonButton>
          </IonItem>
        </IonList>
        <IonListHeader style={{ fontWeight: 'bold', fontSize: '22px' }}>{segment === 'shipping' ? "出荷マスタ : " + count + "件" : "出荷作業完了"}</IonListHeader>
        <ShippingCard
          shipping={shipping}
          hide={segment === 'success'}
        />
        <div style={{ overflowY: 'auto', height: 'calc(100% - 140px)', display: segment === 'shipping' ? 'none' : '' }}>
          <ShippingList
            shippings={shippings}
          />
        </div>
        <IonLoading
          isOpen={loading}
          message={'Loading...'}
          onDidDismiss={() => setLoading(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default connect<OwnProps, StateProps, DispatchProps>({
  mapStateToProps: (state) => ({
    mode: getConfig()!.get('mode'),
    isAuthenticated: state.user.isLoggedin,
  }),
  mapDispatchToProps: {
  },
  component: React.memo(ShippingPage),
});
